import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import { Line } from "react-chartjs-2";
import { FiEdit2, FiGrid, FiPlusCircle, FiPower, FiRefreshCw, FiSave, FiShield, FiTrash2, FiXCircle, FiZap } from "react-icons/fi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import Loader from "../Layout/Loader";
import BentoEmptyState from "../Layout/BentoEmptyState";
import AdminCampaignCreator from "../Sponsored/AdminCampaignCreator";
import { toast } from "react-toastify";
import { getStoredAuthToken } from "../../utils/authSession";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const createEmptyChartData = () => ({
  labels: [],
  datasets: [
    {
      label: "Clicks",
      data: [],
      borderColor: "rgb(75, 192, 192)",
      backgroundColor: "rgba(75, 192, 192, 0.5)",
    },
    {
      label: "Ad Loads",
      data: [],
      borderColor: "rgb(53, 162, 235)",
      backgroundColor: "rgba(53, 162, 235, 0.5)",
    },
    {
      label: "Viewed Impressions",
      data: [],
      borderColor: "rgb(255, 159, 64)",
      backgroundColor: "rgba(255, 159, 64, 0.5)",
    },
  ],
});

const defaultAdminAdForm = {
  adType: "shop",
  shopId: "",
  productId: "",
  brandId: "",
  communityId: "",
  audienceScope: "both",
  customAdText: "",
  customAdLink: "",
  imageUrl: "",
  adSpend: 100,
  adDuration: 7,
  status: "active",
};

const getProductImage = (product) => product?.images?.[0]?.url || "";

const getProductShopId = (product) => product?.shop?._id || product?.shop || product?.shopId || "";

const getBrandShopId = (brand) => brand?.shopOwner?._id || brand?.shopOwner || "";

const getBrandImage = (brand) => brand?.avatarImage?.url || brand?.coverImage?.url || "";

const getCommunityImage = (community) => community?.image || community?.banner?.url || community?.avatar?.url || "";

const getAdSetImage = (ad) => {
  const images = ad?.adSet?.images || {};
  return images["Sponsored Products"] ||
    images["Sponsored Shops"] ||
    images["Sponsored Brands"] ||
    images["Top Banner Ad"] ||
    images.default ||
    "";
};

const getAdType = (ad) => {
  if (ad.product) return "Product";
  if (ad.brand) return "Brand";
  if (ad.community) return "Community";
  if (ad.shop && !ad.product && !ad.brand && !ad.customAdText) return "Shop";
  if (ad.customAdText) return "Custom";
  return "Unknown";
};

const getAdName = (ad) => {
  if (ad.product) return ad.product.name;
  if (ad.brand) return ad.brand.name;
  if (ad.community) return ad.community.name;
  if (ad.shop) return ad.shop.name;
  if (ad.customAdText) return ad.customAdText;
  return "N/A";
};

const formatAudienceScope = (scope) => {
  if (!scope) return "Both";
  return String(scope).charAt(0).toUpperCase() + String(scope).slice(1);
};

const getDateInputValue = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const getAdminRequestConfig = (config = {}) => {
  const token = getStoredAuthToken();

  return {
    ...config,
    withCredentials: true,
    headers: {
      ...config.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
};

const buildAdminAdEditForm = (ad) => ({
  adSpend: ad?.adSpend || 1,
  adDuration: ad?.adDuration || 1,
  status: ad?.status || "active",
  audienceScope: ad?.audienceScope || "both",
  startDate: getDateInputValue(ad?.startDate),
  imageUrl: getAdSetImage(ad),
  customAdText: ad?.customAdText || "",
  customAdLink: ad?.customAdLink || "",
  isCustom: getAdType(ad) === "Custom",
  isCommunity: getAdType(ad) === "Community",
});

const SponsoredPostsAdmin = () => {
  const [sponsoredAds, setSponsoredAds] = useState([]);
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("both"); 
  const [timeResolution, setTimeResolution] = useState("daily");
  const [chartData, setChartData] = useState(createEmptyChartData());
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [adminAdForm, setAdminAdForm] = useState(defaultAdminAdForm);
  const [isCreateSubmitting, setIsCreateSubmitting] = useState(false);
  const [createOptionsError, setCreateOptionsError] = useState("");
  const [editingAdId, setEditingAdId] = useState("");
  const [editingAdForm, setEditingAdForm] = useState(null);
  const [editSubmittingId, setEditSubmittingId] = useState("");
  const [deletePendingAd, setDeletePendingAd] = useState(null);
  const [deleteSubmittingId, setDeleteSubmittingId] = useState("");

  const chartRef = useRef(null);

  const productsForSelectedShop = useMemo(() => {
    if (!adminAdForm.shopId) {
      return products;
    }

    return products.filter((product) => String(getProductShopId(product)) === String(adminAdForm.shopId));
  }, [adminAdForm.shopId, products]);

  const selectedProduct = useMemo(
    () => products.find((product) => String(product._id) === String(adminAdForm.productId)),
    [adminAdForm.productId, products]
  );

  const selectedShop = useMemo(
    () => shops.find((shop) => String(shop._id) === String(adminAdForm.shopId)),
    [adminAdForm.shopId, shops]
  );

  const brandsForSelectedShop = useMemo(() => {
    if (!adminAdForm.shopId) {
      return brands;
    }

    return brands.filter((brand) => String(getBrandShopId(brand)) === String(adminAdForm.shopId));
  }, [adminAdForm.shopId, brands]);

  const selectedBrand = useMemo(
    () => brands.find((brand) => String(brand._id) === String(adminAdForm.brandId)),
    [adminAdForm.brandId, brands]
  );

  const selectedCommunity = useMemo(
    () => communities.find((community) => String(community._id) === String(adminAdForm.communityId)),
    [adminAdForm.communityId, communities]
  );

  const communitiesForSelectedScope = useMemo(() => {
    if (adminAdForm.audienceScope === "both") {
      return communities;
    }

    return communities.filter(
      (community) => String(community.privacy || "public") === String(adminAdForm.audienceScope)
    );
  }, [adminAdForm.audienceScope, communities]);

  useEffect(() => {
    if (!adminAdForm.communityId) {
      return;
    }

    const hasVisibleSelection = communitiesForSelectedScope.some(
      (community) => String(community._id) === String(adminAdForm.communityId)
    );

    if (!hasVisibleSelection) {
      updateAdminAdForm("communityId", "");
    }
  }, [adminAdForm.communityId, communitiesForSelectedScope]);

  const fetchAdminCreateOptions = async () => {
    setCreateOptionsError("");

    try {
      const [shopsResponse, productsResponse, brandsResponse, communitiesResponse] = await Promise.all([
        axios.get(`${server}/shop/admin-all-sellers`, getAdminRequestConfig()),
        axios.get(`${server}/product/admin-all-products`, getAdminRequestConfig()),
        axios.get(`${server}/brand/brands`, getAdminRequestConfig()),
        axios.get(`${server}/sponsored/admin-communities`, getAdminRequestConfig()),
      ]);

      const nextShops = shopsResponse.data?.sellers || [];
      const nextProducts = productsResponse.data?.products || [];
      const nextBrands = brandsResponse.data?.brands || [];
      const nextCommunities = communitiesResponse.data?.communities || [];
      setShops(nextShops);
      setProducts(nextProducts);
      setBrands(nextBrands);
      setCommunities(nextCommunities);
    } catch (error) {
      console.error("Error loading admin ad creator options:", error);
      setCreateOptionsError("Unable to load shops, products, brands, and communities for admin ad creation.");
    }
  };

  const generateKey = (date, resolution) => {
    if (resolution === "daily") {
      return date.toISOString().split("T")[0];
    }
    return `${date.toISOString().split("T")[0]} ${date.getHours()}:00`;
  };

  // 4) Build the multi-metric chart from combined ads
  const processChartData = useCallback((ads, filter) => {
    const filteredAds = ads.filter((ad) => {
      if (filter === "both") return true;
      if (filter === "product") return !!ad.product;
      if (filter === "shop") return ad.shop && !ad.product && !ad.brand && !ad.community && !ad.customAdText;
      if (filter === "brand") return !!ad.brand;
      if (filter === "community") return !!ad.community;
      if (filter === "custom") return !!ad.customAdText;
      return false;
    });

    const aggregationMap = {};

    filteredAds.forEach((ad) => {
      const clicksArr = ad.metrics?.clicks || [];
      const adLoadsArr = ad.metrics?.adLoads || [];
      const viewedArr = ad.metrics?.viewedImpressions || [];

      clicksArr.forEach((clickObj) => {
        const date = new Date(clickObj.timestamp);
        const key = generateKey(date, timeResolution);
        if (!aggregationMap[key]) {
          aggregationMap[key] = { clicks: 0, adLoads: 0, viewedImpressions: 0 };
        }
        aggregationMap[key].clicks += 1;
      });

      adLoadsArr.forEach((loadObj) => {
        const date = new Date(loadObj.timestamp);
        const key = generateKey(date, timeResolution);
        if (!aggregationMap[key]) {
          aggregationMap[key] = { clicks: 0, adLoads: 0, viewedImpressions: 0 };
        }
        aggregationMap[key].adLoads += 1;
      });

      viewedArr.forEach((impressObj) => {
        const date = new Date(impressObj.timestamp);
        const key = generateKey(date, timeResolution);
        if (!aggregationMap[key]) {
          aggregationMap[key] = { clicks: 0, adLoads: 0, viewedImpressions: 0 };
        }
        aggregationMap[key].viewedImpressions += 1;
      });
    });

    const labels = Object.keys(aggregationMap).sort((a, b) => new Date(a) - new Date(b));
    const clicksData = labels.map((label) => aggregationMap[label].clicks);
    const adLoadsData = labels.map((label) => aggregationMap[label].adLoads);
    const viewedData = labels.map((label) => aggregationMap[label].viewedImpressions);

    setChartData({
      labels,
      datasets: [
        {
          label: "Clicks",
          data: clicksData,
          borderColor: "rgb(75, 192, 192)",
          backgroundColor: "rgba(75, 192, 192, 0.5)",
        },
        {
          label: "Ad Loads",
          data: adLoadsData,
          borderColor: "rgb(53, 162, 235)",
          backgroundColor: "rgba(53, 162, 235, 0.5)",
        },
        {
          label: "Viewed Impressions",
          data: viewedData,
          borderColor: "rgb(255, 159, 64)",
          backgroundColor: "rgba(255, 159, 64, 0.5)",
        },
      ],
    });
  }, [timeResolution]);

  // 1) Fetch base sponsored ads
  const fetchSponsoredAds = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.get(`${server}/sponsored/sponsored-ads-all`, getAdminRequestConfig({
        headers: { "Content-Type": "application/json" },
        params: { filter: selectedFilter },
      }));
      console.log("Fetched Sponsored Ads Response:", response.data);

      const adList = response.data || [];
      if (!adList.length) {
        setSponsoredAds([]);
        setChartData(createEmptyChartData());
        return;
      }

      const adIds = adList.map((ad) => ad._id);

      const metricsResponse = await axios.post(
        `${server}/sponsored/metrics/batch-admin`,
        { adIds },
        getAdminRequestConfig({
          headers: { "Content-Type": "application/json" },
        })
      );
      console.log("Fetched Admin Metrics:", metricsResponse.data);

      const metricsMap = metricsResponse.data || {};
      const combinedAds = adList.map((ad) => {
        const adMetrics = metricsMap[ad._id] || {
          clicks: [],
          viewedImpressions: [],
          adLoads: [],
        };
        return {
          ...ad,
          metrics: adMetrics,
        };
      });

      setSponsoredAds(combinedAds);
      processChartData(combinedAds, selectedFilter);
    } catch (error) {
      console.error("Error fetching sponsored ads:", error);
      setErrorMessage("Failed to load sponsored ads. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [processChartData, selectedFilter]);

  useEffect(() => {
    fetchSponsoredAds();
  }, [fetchSponsoredAds]);

  useEffect(() => {
    fetchAdminCreateOptions();
  }, []);

  // Chart Options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: "Sponsored Ads Performance",
        font: {
          size: 20,
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y}`,
        },
      },
      legend: {
        position: "top",
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: timeResolution === "daily" ? "Date" : "Date & Hour",
        },
      },
      y: {
        title: {
          display: true,
          text: "Count",
        },
        beginAtZero: true,
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        hoverRadius: 7,
        radius: 5,
      },
    },
  };

  // Filter button clicks
  const handleFilterChange = (newFilter) => {
    setSelectedFilter(newFilter);
  };

  // Toggle ad active/inactive
  const toggleAdStatus = async (adId, isActive) => {
    const nextIsActive = !isActive;
    try {
      await axios.post(
        `${server}/sponsored/toggle-status`,
        { adId, isActive: nextIsActive },
        getAdminRequestConfig()
      );
      setSponsoredAds((prevAds) =>
        prevAds.map((ad) =>
          ad._id === adId ? { ...ad, isActive: nextIsActive, status: nextIsActive ? "active" : "paused" } : ad
        )
      );
    } catch (error) {
      console.error("Failed to toggle ad status:", error);
      setErrorMessage("Error toggling ad status. Please try again.");
      toast.error("Error toggling ad status.");
    }
  };

  const handleStartEditAd = (ad) => {
    setEditingAdId(ad._id);
    setEditingAdForm(buildAdminAdEditForm(ad));
  };

  const handleCancelEditAd = () => {
    setEditingAdId("");
    setEditingAdForm(null);
  };

  const updateEditingAdForm = (field, value) => {
    setEditingAdForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const handleSaveAdminAd = async (event) => {
    event.preventDefault();

    if (!editingAdId || !editingAdForm) {
      return;
    }

    const payload = {
      adSpend: Number(editingAdForm.adSpend),
      adDuration: Number(editingAdForm.adDuration),
      status: editingAdForm.status,
      imageUrl: editingAdForm.imageUrl,
      audienceScope: editingAdForm.audienceScope,
    };

    if (editingAdForm.startDate) {
      payload.startDate = editingAdForm.startDate;
    }

    if (editingAdForm.isCustom) {
      payload.customAdText = editingAdForm.customAdText;
      payload.customAdLink = editingAdForm.customAdLink;
    }

    setEditSubmittingId(editingAdId);

    try {
      await axios.patch(
        `${server}/sponsored/admin-update-ad/${editingAdId}`,
        payload,
        getAdminRequestConfig()
      );

      toast.success("Admin ad updated successfully.");
      handleCancelEditAd();
      await fetchSponsoredAds();
    } catch (error) {
      console.error("Error updating admin ad:", error);
      toast.error(error.response?.data?.message || "Unable to update admin ad.");
    } finally {
      setEditSubmittingId("");
    }
  };

  const updateAdminAdForm = (field, value) => {
    setAdminAdForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const handleProductSelect = (productId) => {
    const product = products.find((item) => String(item._id) === String(productId));

    setAdminAdForm((currentForm) => ({
      ...currentForm,
      productId,
      shopId: getProductShopId(product) ? String(getProductShopId(product)) : currentForm.shopId,
    }));
  };

  const handleBrandSelect = (brandId) => {
    const brand = brands.find((item) => String(item._id) === String(brandId));

    setAdminAdForm((currentForm) => ({
      ...currentForm,
      brandId,
      shopId: getBrandShopId(brand) ? String(getBrandShopId(brand)) : currentForm.shopId,
    }));
  };

  const handleCommunitySelect = (communityId) => {
    setAdminAdForm((currentForm) => ({
      ...currentForm,
      communityId,
    }));
  };

  const handleRequestDeleteAd = (ad) => {
    setDeletePendingAd(ad);
  };

  const handleCancelDeleteAd = () => {
    if (deleteSubmittingId) {
      return;
    }

    setDeletePendingAd(null);
  };

  const handleConfirmDeleteAd = async () => {
    if (!deletePendingAd?._id) {
      return;
    }

    setDeleteSubmittingId(deletePendingAd._id);

    try {
      await axios.patch(
        `${server}/sponsored/admin-delete-ad/${deletePendingAd._id}`,
        {},
        getAdminRequestConfig()
      );

      setSponsoredAds((currentAds) => {
        const nextAds = currentAds.filter((ad) => ad._id !== deletePendingAd._id);
        processChartData(nextAds, selectedFilter);
        return nextAds;
      });

      if (editingAdId === deletePendingAd._id) {
        handleCancelEditAd();
      }

      toast.success("Ad removed from the active list.");
      setDeletePendingAd(null);
    } catch (error) {
      console.error("Error deleting admin ad:", error);
      toast.error(error.response?.data?.message || "Unable to delete admin ad.");
    } finally {
      setDeleteSubmittingId("");
    }
  };

  const handleCreateAdminAd = async (event) => {
    event.preventDefault();

    if (adminAdForm.adType === "shop" && !adminAdForm.shopId) {
      toast.error("Choose a shop before creating a shop ad.");
      return;
    }

    if (adminAdForm.adType === "product" && !adminAdForm.productId) {
      toast.error("Choose a product before creating a product ad.");
      return;
    }

    if (adminAdForm.adType === "brand" && !adminAdForm.brandId) {
      toast.error("Choose a brand before creating a brand ad.");
      return;
    }

    if (adminAdForm.adType === "community" && !adminAdForm.communityId) {
      toast.error("Choose a community before creating a community ad.");
      return;
    }

    if (adminAdForm.adType === "custom" && (!adminAdForm.customAdText || !adminAdForm.customAdLink)) {
      toast.error("Add custom ad text and a destination link.");
      return;
    }

    setIsCreateSubmitting(true);

    try {
      await axios.post(
        `${server}/sponsored/admin-create-ad`,
        {
          ...adminAdForm,
          adSpend: Number(adminAdForm.adSpend),
          adDuration: Number(adminAdForm.adDuration),
        },
        getAdminRequestConfig()
      );

      toast.success("Admin ad created successfully.");
      setAdminAdForm((currentForm) => ({
        ...defaultAdminAdForm,
        shopId: currentForm.shopId,
      }));
      await fetchSponsoredAds();
    } catch (error) {
      console.error("Error creating admin ad:", error);
      toast.error(error.response?.data?.message || "Unable to create admin ad.");
    } finally {
      setIsCreateSubmitting(false);
    }
  };

  const previewImageUrl =
    adminAdForm.imageUrl ||
    getProductImage(selectedProduct) ||
    getBrandImage(selectedBrand) ||
    getCommunityImage(selectedCommunity) ||
    selectedShop?.banner?.url ||
    selectedShop?.avatar?.url ||
    "";

  return (
    <div className="sponsored-posts-admin">
      <section className="admin-dashboard-orders__section sponsored-posts-admin-create-section">
        <div className="sponsored-posts-admin-create-heading">
          <div className="sponsored-posts-admin-title-stack">
            <h2 className="admin-section-title-row admin-dashboard-orders__title">
              <FiPlusCircle aria-hidden="true" />
              <span>Admin Ad Creator</span>
            </h2>
            <span className="admin-section-title-row sponsored-posts-admin-role-pill">
              <FiShield aria-hidden="true" />
              <span>Super Admin</span>
            </span>
          </div>
          <button
            type="button"
            className="sponsored-posts-admin-refresh-button buzzvibe-green-button"
            onClick={() => fetchSponsoredAds()}
          >
            <FiRefreshCw aria-hidden="true" />
            Refresh Ads
          </button>
        </div>

        {createOptionsError && <p className="sponsored-posts-admin-create-error">{createOptionsError}</p>}

        <div className="sponsored-posts-admin-tool-panel sponsored-posts-admin-campaign-creator-panel">
          <div className="sponsored-posts-admin-tool-header">
            <h3 className="admin-section-title-row sponsored-posts-admin-tool-title">
              <FiGrid aria-hidden="true" />
              <span>Campaign Builder</span>
            </h3>
          </div>
          <div className="sponsored-posts-admin-campaign-creator">
            <AdminCampaignCreator
              adminOptions={{ shops, products, brands, communities }}
              onCampaignCreated={fetchSponsoredAds}
            />
          </div>
        </div>

        <div className="sponsored-posts-admin-tool-panel sponsored-posts-admin-quick-create-panel">
          <div className="sponsored-posts-admin-tool-header">
            <h3 className="admin-section-title-row sponsored-posts-admin-tool-title">
              <FiZap aria-hidden="true" />
              <span>Quick Create</span>
            </h3>
          </div>
          <form className="sponsored-posts-admin-create-form sponsored-posts-admin-quick-create-form" onSubmit={handleCreateAdminAd}>
            <label className="sponsored-posts-admin-create-field">
              <span>Ad Type</span>
              <select
                value={adminAdForm.adType}
                onChange={(event) => {
                  const nextType = event.target.value;
                  setAdminAdForm((currentForm) => ({
                    ...currentForm,
                    adType: nextType,
                    productId: nextType === "product" ? currentForm.productId : "",
                    brandId: nextType === "brand" ? currentForm.brandId : "",
                    communityId: nextType === "community" ? currentForm.communityId : "",
                    customAdText: nextType === "custom" ? currentForm.customAdText : "",
                    customAdLink: nextType === "custom" ? currentForm.customAdLink : "",
                  }));
                }}
              >
                <option value="shop">Shop Ad</option>
                <option value="product">Product Ad</option>
                <option value="brand">Brand Ad</option>
                <option value="community">Community Ad</option>
                <option value="custom">Custom Ad</option>
              </select>
            </label>

            <label className="sponsored-posts-admin-create-field">
              <span>Assign Business</span>
              <select
                value={adminAdForm.shopId}
                onChange={(event) => updateAdminAdForm("shopId", event.target.value)}
              >
                <option value="">Platform / no shop</option>
                {shops.map((shop) => (
                  <option key={shop._id} value={shop._id}>{shop.name}</option>
                ))}
              </select>
            </label>

            {adminAdForm.adType === "community" && (
              <label className="sponsored-posts-admin-create-field">
                <span>Community Reach</span>
                <select
                  value={adminAdForm.audienceScope}
                  onChange={(event) => updateAdminAdForm("audienceScope", event.target.value)}
                >
                  <option value="both">Public + Private</option>
                  <option value="public">Public only</option>
                  <option value="private">Private only</option>
                </select>
              </label>
            )}

            {adminAdForm.adType === "product" && (
              <label className="sponsored-posts-admin-create-field sponsored-posts-admin-create-field--wide">
                <span>Product</span>
                <select
                  value={adminAdForm.productId}
                  onChange={(event) => handleProductSelect(event.target.value)}
                >
                  <option value="">Choose product</option>
                  {productsForSelectedShop.map((product) => (
                    <option key={product._id} value={product._id}>{product.name}</option>
                  ))}
                </select>
              </label>
            )}

            {adminAdForm.adType === "brand" && (
              <label className="sponsored-posts-admin-create-field sponsored-posts-admin-create-field--wide">
                <span>Brand</span>
                <select
                  value={adminAdForm.brandId}
                  onChange={(event) => handleBrandSelect(event.target.value)}
                >
                  <option value="">Choose brand</option>
                  {brandsForSelectedShop.map((brand) => (
                    <option key={brand._id} value={brand._id}>{brand.name}</option>
                  ))}
                </select>
              </label>
            )}

              {adminAdForm.adType === "community" && (
                <label className="sponsored-posts-admin-create-field sponsored-posts-admin-create-field--wide">
                  <span>Community</span>
                  <select
                    value={adminAdForm.communityId}
                    onChange={(event) => handleCommunitySelect(event.target.value)}
                  >
                    <option value="">Choose community</option>
                    {communitiesForSelectedScope.map((community) => (
                      <option key={community._id} value={community._id}>
                        {community.name} · {(community.privacy || "public").toUpperCase()} · {community.memberCount || 0} members
                      </option>
                    ))}
                  </select>
                </label>
              )}

            {adminAdForm.adType === "custom" && (
              <>
                <label className="sponsored-posts-admin-create-field sponsored-posts-admin-create-field--wide">
                  <span>Custom Text</span>
                  <input
                    type="text"
                    value={adminAdForm.customAdText}
                    onChange={(event) => updateAdminAdForm("customAdText", event.target.value)}
                    placeholder="Sponsored message"
                  />
                </label>
                <label className="sponsored-posts-admin-create-field sponsored-posts-admin-create-field--wide">
                  <span>Destination Link</span>
                  <input
                    type="url"
                    value={adminAdForm.customAdLink}
                    onChange={(event) => updateAdminAdForm("customAdLink", event.target.value)}
                    placeholder="https://www.buzzvibe.com"
                  />
                </label>
              </>
            )}

            <label className="sponsored-posts-admin-create-field sponsored-posts-admin-create-field--wide">
              <span>Image URL</span>
              <input
                type="url"
                value={adminAdForm.imageUrl}
                onChange={(event) => updateAdminAdForm("imageUrl", event.target.value)}
                placeholder="Optional campaign image override"
              />
            </label>

            <label className="sponsored-posts-admin-create-field">
              <span>Ad Spend</span>
              <input
                type="number"
                min="1"
                value={adminAdForm.adSpend}
                onChange={(event) => updateAdminAdForm("adSpend", event.target.value)}
              />
            </label>

            <label className="sponsored-posts-admin-create-field">
              <span>Duration</span>
              <input
                type="number"
                min="1"
                max="30"
                value={adminAdForm.adDuration}
                onChange={(event) => updateAdminAdForm("adDuration", event.target.value)}
              />
            </label>

            <label className="sponsored-posts-admin-create-field">
              <span>Status</span>
              <select
                value={adminAdForm.status}
                onChange={(event) => updateAdminAdForm("status", event.target.value)}
              >
                <option value="active">Active</option>
                <option value="inReview">In Review</option>
                <option value="paused">Paused</option>
              </select>
            </label>

            <div className="sponsored-posts-admin-create-preview">
              {previewImageUrl ? (
                <img src={previewImageUrl} alt="Admin ad preview" />
              ) : (
                <span>No Image</span>
              )}
              <strong>
                {selectedProduct?.name || selectedBrand?.name || selectedCommunity?.name || selectedShop?.name || adminAdForm.customAdText || "New sponsored ad"}
              </strong>
              {adminAdForm.adType === "community" && selectedCommunity && (
                <span>{formatAudienceScope(adminAdForm.audienceScope)} communities</span>
              )}
            </div>

            <button
              type="submit"
              className="sponsored-posts-admin-create-submit buzzvibe-green-button"
              disabled={isCreateSubmitting}
            >
              <FiPlusCircle aria-hidden="true" />
              {isCreateSubmitting ? "Creating" : "Create Ad"}
            </button>
          </form>
        </div>
      </section>

      {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

      {isLoading ? (
        <Loader />
      ) : !errorMessage && sponsoredAds.length === 0 ? (
        <BentoEmptyState
          title="No sponsored ads available"
          description="Performance reporting will appear here once a sponsored campaign has data to display."
          tiles={[
            {
              title: "Products",
              description: "No sponsored product campaigns are active right now.",
            },
            {
              title: "Shops",
              description: "No sponsored shop placements are currently running.",
            },
            {
              title: "Brands",
              description: "Brand sponsorships will populate here after launch.",
            },
            {
              title: "Communities",
              description: "Private and public community promotions will appear here once launched.",
            },
            {
              title: "Metrics",
              description: "Clicks, loads, and impressions will appear after the first ad run.",
            },
          ]}
        />
      ) : (
        <>
          {/* Filter & Time Row */}
          <div className="sponsored-dashboard-container">
            <div className="sponsored-dashboard-filter-time-row">
              {/* Filter Buttons */}
              <div className="sponsored-dashboard-filter-row">
                <div
                  className={`sponsored-dashboard-filter-item ${
                    selectedFilter === "both" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("both")}
                >
                  All
                </div>
                <div
                  className={`sponsored-dashboard-filter-item ${
                    selectedFilter === "product" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("product")}
                >
                  Products
                </div>
                <div
                  className={`sponsored-dashboard-filter-item ${
                    selectedFilter === "shop" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("shop")}
                >
                  Shops
                </div>
                <div
                  className={`sponsored-dashboard-filter-item ${
                    selectedFilter === "brand" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("brand")}
                >
                  Brands
                </div>
                <div
                  className={`sponsored-dashboard-filter-item ${
                    selectedFilter === "community" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("community")}
                >
                  Communities
                </div>
                <div
                  className={`sponsored-dashboard-filter-item ${
                    selectedFilter === "custom" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("custom")}
                >
                  Custom
                </div>
              </div>

              {/* Time Buttons */}
              <div className="sponsored-dashboard-time-buttons-row">
                <div
                  className={`sponsored-dashboard-time-filter-item ${
                    timeResolution === "daily" ? "active" : ""
                  }`}
                  onClick={() => setTimeResolution("daily")}
                >
                  Daily View
                </div>
                <div
                  className={`sponsored-dashboard-time-filter-item ${
                    timeResolution === "hourly" ? "active" : ""
                  }`}
                  onClick={() => setTimeResolution("hourly")}
                >
                  Hourly View
                </div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="admin-dashboard-orders__section chart-container sponsored-posts-admin-chart-container">
            <Line ref={chartRef} data={chartData} options={chartOptions} />
          </div>

          {/* Table */}
          <section className="admin-dashboard-orders__section sponsored-posts-admin-table-section">
            <h2 className="admin-dashboard-orders__title">Sponsored Ads</h2>
            <div className="admin-dashboard-orders__table-container">
            <table className="admin-dashboard-orders__table sponsored-table">
              <thead className="admin-dashboard-orders__thead">
                <tr>
                  <th>Shop</th>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Audience</th>
                  <th>Image</th>
                  <th>Ad Spend</th>
                  <th>Ad Duration</th>
                  <th>Spent So Far</th>
                  <th>Status</th>
                  <th>Clicks</th>
                  <th>Impressions</th>
                  <th>CPC</th>
                  <th>CPI</th>
                  <th>Manage</th>
                </tr>
              </thead>
              <tbody className="admin-dashboard-orders__tbody">
                {sponsoredAds.map((ad) => {
                  const type = getAdType(ad);
                  const name = getAdName(ad);
                  const isEditingAd = editingAdId === ad._id && editingAdForm;

                  // For images
                  const imageUrl =
                    getAdSetImage(ad) ||
                    ad.product?.images?.[0]?.url ||
                    getCommunityImage(ad.community) ||
                    ad.shop?.avatar?.url ||
                    ad.brand?.avatarImage?.url ||
                    ad.customAdImage?.url ||
                    "";

                  // metrics
                  const clicks = ad.metrics?.clicks?.length || 0;
                  const impressions = ad.metrics?.viewedImpressions?.length || 0;
                  // if adLoads needed => const adLoadsCount = ad.metrics?.adLoads?.length || 0;

                  const spentSoFar = ad.spentSoFar || 0;
                  const cpc = clicks > 0 ? (spentSoFar / clicks).toFixed(2) : "0.00";
                  const cpi = impressions > 0 ? (spentSoFar / impressions).toFixed(2) : "0.00";

                  return (
                    <React.Fragment key={ad._id}>
                      <tr className="admin-dashboard-orders__tr">
                        <td>{ad.shop?.name || "N/A"}</td>
                        <td>{type}</td>
                        <td>{name}</td>
                        <td>{ad.community ? formatAudienceScope(ad.audienceScope) : "-"}</td>
                        <td>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={type}
                              className="sponsored-posts-admin-table-image"
                            />
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>${ad.adSpend?.toFixed(2) || "0.00"}</td>
                        <td>{ad.adDuration || "N/A"}</td>
                        <td>${spentSoFar.toFixed(2)}</td>
                        <td>
                          <span className={`sponsored-posts-admin-status-pill sponsored-posts-admin-status-pill--${String(ad.status || "unknown").toLowerCase()}`}>
                            {ad.status || "N/A"}
                          </span>
                        </td>
                        <td>{clicks}</td>
                        <td>{impressions}</td>
                        <td>${cpc}</td>
                        <td>${cpi}</td>
                        <td>
                          <div className="sponsored-posts-admin-action-cell">
                            <div className="sponsored-posts-admin-action-group sponsored-posts-admin-action-rail">
                              <button
                                type="button"
                                className={`sponsored-posts-admin-toggle-button ${ad.isActive ? "sponsored-posts-admin-toggle-button--active" : "buzzvibe-green-button"}`}
                                onClick={() => toggleAdStatus(ad._id, ad.isActive)}
                                aria-label={`Toggle ad status for ${name}`}
                              >
                                <FiPower aria-hidden="true" />
                                {ad.isActive ? "Deactivate" : "Activate"}
                              </button>
                              <button
                                type="button"
                                className="sponsored-posts-admin-toggle-button"
                                onClick={() => handleStartEditAd(ad)}
                                aria-label={`Edit ad settings for ${name}`}
                              >
                                <FiEdit2 aria-hidden="true" />
                                Edit
                              </button>
                              <button
                                type="button"
                                className="sponsored-posts-admin-toggle-button sponsored-posts-admin-toggle-button--active"
                                onClick={() => handleRequestDeleteAd(ad)}
                                aria-label={`Remove ad campaign ${name}`}
                              >
                                <FiTrash2 aria-hidden="true" />
                                Remove
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {isEditingAd && (
                        <tr className="admin-dashboard-orders__tr sponsored-posts-admin-edit-row">
                          <td colSpan="14">
                            <form className="sponsored-posts-admin-edit-form" onSubmit={handleSaveAdminAd}>
                              <label className="sponsored-posts-admin-create-field">
                                <span>Ad Spend</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={editingAdForm.adSpend}
                                  onChange={(event) => updateEditingAdForm("adSpend", event.target.value)}
                                />
                              </label>
                              <label className="sponsored-posts-admin-create-field">
                                <span>Duration</span>
                                <input
                                  type="number"
                                  min="1"
                                  max="30"
                                  value={editingAdForm.adDuration}
                                  onChange={(event) => updateEditingAdForm("adDuration", event.target.value)}
                                />
                              </label>
                              <label className="sponsored-posts-admin-create-field">
                                <span>Status</span>
                                <select
                                  value={editingAdForm.status}
                                  onChange={(event) => updateEditingAdForm("status", event.target.value)}
                                >
                                  <option value="processing">Processing</option>
                                  <option value="active">Active</option>
                                  <option value="inReview">In Review</option>
                                  <option value="paused">Paused</option>
                                  <option value="completed">Completed</option>
                                  <option value="cancelled">Cancelled</option>
                                  <option value="rejected">Rejected</option>
                                </select>
                              </label>
                              {editingAdForm.isCommunity && (
                                <label className="sponsored-posts-admin-create-field">
                                  <span>Community Reach</span>
                                  <select
                                    value={editingAdForm.audienceScope}
                                    onChange={(event) => updateEditingAdForm("audienceScope", event.target.value)}
                                  >
                                    <option value="both">Public + Private</option>
                                    <option value="public">Public only</option>
                                    <option value="private">Private only</option>
                                  </select>
                                </label>
                              )}
                              <label className="sponsored-posts-admin-create-field">
                                <span>Start Date</span>
                                <input
                                  type="date"
                                  value={editingAdForm.startDate}
                                  onChange={(event) => updateEditingAdForm("startDate", event.target.value)}
                                />
                              </label>
                              <label className="sponsored-posts-admin-create-field sponsored-posts-admin-create-field--wide">
                                <span>Image URL</span>
                                <input
                                  type="url"
                                  value={editingAdForm.imageUrl}
                                  onChange={(event) => updateEditingAdForm("imageUrl", event.target.value)}
                                />
                              </label>
                              {editingAdForm.isCustom && (
                                <>
                                  <label className="sponsored-posts-admin-create-field">
                                    <span>Custom Text</span>
                                    <input
                                      type="text"
                                      value={editingAdForm.customAdText}
                                      onChange={(event) => updateEditingAdForm("customAdText", event.target.value)}
                                    />
                                  </label>
                                  <label className="sponsored-posts-admin-create-field sponsored-posts-admin-create-field--wide">
                                    <span>Destination Link</span>
                                    <input
                                      type="url"
                                      value={editingAdForm.customAdLink}
                                      onChange={(event) => updateEditingAdForm("customAdLink", event.target.value)}
                                    />
                                  </label>
                                </>
                              )}
                              <div className="sponsored-posts-admin-edit-actions">
                                <button
                                  type="submit"
                                  className="sponsored-posts-admin-create-submit buzzvibe-green-button"
                                  disabled={editSubmittingId === ad._id}
                                >
                                  <FiSave aria-hidden="true" />
                                  {editSubmittingId === ad._id ? "Saving" : "Save"}
                                </button>
                                <button
                                  type="button"
                                  className="sponsored-posts-admin-toggle-button"
                                  onClick={handleCancelEditAd}
                                  disabled={editSubmittingId === ad._id}
                                >
                                  <FiXCircle aria-hidden="true" />
                                  Cancel
                                </button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          </section>
        </>
      )}

      {deletePendingAd && (
        <div className="sponsored-posts-admin-confirm-backdrop" onClick={handleCancelDeleteAd}>
          <div className="sponsored-posts-admin-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sponsored-posts-admin-confirm-icon">
              <FiTrash2 aria-hidden="true" />
            </div>
            <h3>Remove ad campaign?</h3>
            <p>
              {getAdName(deletePendingAd)} will stay timestamped in the database, but it will be removed from the active admin list.
            </p>
            <div className="sponsored-posts-admin-confirm-actions">
              <button
                type="button"
                className="sponsored-posts-admin-toggle-button"
                onClick={handleCancelDeleteAd}
                disabled={deleteSubmittingId === deletePendingAd._id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sponsored-posts-admin-toggle-button sponsored-posts-admin-toggle-button--active"
                onClick={handleConfirmDeleteAd}
                disabled={deleteSubmittingId === deletePendingAd._id}
              >
                <FiTrash2 aria-hidden="true" />
                {deleteSubmittingId === deletePendingAd._id ? "Removing" : "Approve Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsoredPostsAdmin;
